import { supabase } from "./supabase";

// ─────────────────────────────────────────────────────────────────────────────
// Weekend squad-drift client (round 13, R18). Three admin RPCs and the
// squad-drift-check Edge Function. Nothing here throws: success is
// `{ ok: true, ...data }`, failure is `{ ok: false, error, status }` where
// `error` is the server token (not_pending, stale_item, feed_unavailable,
// unauthenticated, forbidden, server_error) or `network`, and `status` is the
// HTTP status when there was one (else null).
// ─────────────────────────────────────────────────────────────────────────────

const RPC_TOKENS = ["not_pending", "stale_item", "forbidden"];
const FUNCTION_TOKENS = new Set(["unauthenticated", "forbidden", "invalid_body", "feed_unavailable", "method_not_allowed", "server_error"]);

function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function rpcFailure(error, status) {
  const message = String(error?.message || "");
  const token = error?.code === "42501" ? "forbidden" : RPC_TOKENS.find((t) => message.startsWith(t));
  return { ok: false, error: token ?? "server_error", status: status ?? null };
}

// Settle one RPC call into the module's result shape (never throws).
async function settle(call) {
  try {
    const { data, error, status } = await call();
    if (error) return rpcFailure(error, status);
    if (!isObject(data)) return { ok: false, error: "server_error", status: status ?? null };
    return { ...data, ok: true };
  } catch {
    return { ok: false, error: "network", status: null };
  }
}

/** Pending squad changes: `{ ok: true, season, last_run_at, items[] }`. */
export async function fetchSquadDrift(season) {
  const res = await settle(() => supabase.rpc("admin_squad_drift", season == null ? {} : { p_season: season }));
  if (res.ok && !Array.isArray(res.items)) return { ok: false, error: "server_error", status: null };
  return res;
}

/** Move: replace the player's season squads with Rugby League Project's. */
export function applySquadDrift(itemId) {
  return settle(() => supabase.rpc("admin_apply_squad_drift", { p_item_id: itemId }));
}

/** Ignore: hide this change. */
export function ignoreSquadDrift(itemId) {
  return settle(() => supabase.rpc("admin_ignore_squad_drift", { p_item_id: itemId }));
}

/**
 * Run the weekend check now (squad-drift-check, with the admin's session —
 * modelled on runSquadBuild). `{ ok: true, season, new_items, pending, … }` or
 * `{ ok: false, error, status }`.
 */
export async function runSquadDriftCheck({ season } = {}) {
  let response;
  try {
    response = await supabase.functions.invoke("squad-drift-check", { body: season != null ? { season } : {} });
  } catch {
    return { ok: false, error: "network", status: null };
  }
  if (response?.error) {
    const context = response.error.context;
    if (context && typeof context.json === "function") {
      const status = Number.isFinite(context.status) ? context.status : null;
      let token = "server_error";
      try {
        const body = await context.json();
        if (FUNCTION_TOKENS.has(body?.error)) token = body.error;
      } catch { /* an unreadable body is a server_error */ }
      return { ok: false, error: token, status };
    }
    return { ok: false, error: "network", status: null };
  }
  const data = response?.data;
  if (!isObject(data)) return { ok: false, error: "server_error", status: null };
  return { ...data, ok: true };
}
